import {
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  QueryList,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import {
  AbstractControl,
  UntypedFormBuilder,
  UntypedFormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { of, Subscription } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { LOCALIDADES_BOGOTA } from '../../../comun/data/localidades-bogota';
import { Localidad } from '../../../comun/models/localidad';
import { ParametrosService } from '../../../services/parametros.service';
import { SweetAlertService } from '../../../services/sweet-alert.service';
import { UserService } from '../../../services/user.service';
import { PERFIL_COPY } from '../../data/perfil-microempresario.content';
import { PerfilDocumentosService } from '../documentos/perfil-documentos.service';
import {
  PERFIL_DOC_PASO,
  acceptFromExtensiones,
  baseValueKeyFromSlot,
  cantidadSlotsDoc,
  isMultimediaDoc,
  isMultipleDoc,
  labelConOpcional,
  intersectSeccionDocsConSolicitud,
  pruneStaleDocControls,
  formatBytesLabel,
  slotsForDoc,
} from '../documentos/perfil-documentos.util';
import type { PerfilDocSlot } from '../documentos/perfil-documentos.util';
import {
  canRemoveArchivoSlot,
  emptyArchivoSlot,
  isArchivoSlotRechazado,
  isArchivoSlotValidoParaProgreso,
  PerfilArchivoSlotState,
  shouldShowArchivoSlotAprobadoUi,
  slotStateFromUploadResult,
  slotStateFromUploaded,
} from '../documentos/perfil-archivo-slot.util';
import {
  findRedDetalleForCatalogo,
  mapEmpresarioToSobreNegocio,
} from '../progress/perfil-progress-hydrate';
import { PerfilDocumentoDef } from '../progress/perfil-progress.definitions';
import { PerfilProgressService } from '../progress/perfil-progress.service';
import {
  PERFIL_HUB_ROUTE,
  PERFIL_NAV_SHOW_INTRO,
  PERFIL_PASO_QUERY,
  consumeShowIntroFlag,
  invalidFilledControlKeys,
  markInvalidControlsTouched,
  navigateAfterSectionSave,
  parsePerfilPasoQuery,
  readPerfilNavState,
  resolveNextSectionId,
  resolvePerfilStepCtaKind,
} from '../progress/perfil-section-nav';
import { PerfilSeccionAccesoService } from '../progress/perfil-seccion-acceso.service';
import {
  PerfilSectionDirtyTracker,
  serializePerfilSectionState,
} from '../progress/perfil-section-dirty';
import { PerfilSegmentoService } from '../segmento/perfil-segmento.service';
import { isFechaRegistroCamaraInvalida } from '../segmento/perfil-segmento.util';
import { PerfilDocumentoModalConfig } from '../shared/perfil-documento-modal/perfil-documento-modal.model';
import { PerfilSolicitudService } from '../solicitud/perfil-solicitud.service';
import { PerfilEmpresarioCache } from '../solicitud/perfil-solicitud.models';
import { AnalyticsTagService } from 'src/app/services/analytics-tag.service';

declare var $: any;

/** Antepone https:// solo si la URL no trae ya http:// o https://. */
function normalizeRedSocialUrl(url: string): string {
  const trimmed = url.trim();
  if (!trimmed) {
    return '';
  }
  if (/^https?:\/\//i.test(trimmed)) {
    return trimmed;
  }
  return `https://${trimmed}`;
}

/** Subpaso dentro de la sección (varios formularios / pestañas). */
interface SobreNegocioStep {
  id: string;
  /** Controles obligatorios que deben validarse para Continuar / Guardar. */
  controls: string[];
}

interface RedSocialItem {
  id: string;
  label: string;
  idCatalogo?: number;
  esOtro?: boolean;
}

interface CatalogoRedSocialApi {
  idCatalogoRedSocial?: number;
  codigo?: string;
  nombre?: string;
  orden?: number;
  esOtro?: boolean;
}

interface CatalogoItem {
  id: number;
  nombre: string;
}

const ID_BOGOTA = 11001;

/**
 * Formulario "Sobre tu negocio" — validaciones de campos obligatorios
 * alineadas a solicitud PN / Sobre ti.
 *
 * Sección multi-paso: intermedios dicen Continuar; el último dice Guardar
 * información (o Continuar si ya está al 100 % sin cambios). Solo se llama
 * al back cuando hay modificaciones.
 */
@Component({
  selector: 'app-perfil-sobre-negocio',
  templateUrl: './perfil-sobre-negocio.component.html',
  styleUrls: ['./perfil-sobre-negocio.component.scss'],
  standalone: false,
})
export class PerfilSobreNegocioComponent implements OnInit, OnDestroy {
  readonly copy = PERFIL_COPY;
  readonly fotoArchivoIcon =
    'assets/img/perfil/icon-archivo-cargado.svg';
  readonly checkIcon = 'assets/img/perfil/icon-check.svg';
  /** Redes solo desde API de catálogo (habilitadas). */
  redesItems: RedSocialItem[] = [];

  userName = '';
  form!: UntypedFormGroup;
  /** Estado UI de fotos (nombre + tamaño), alineado a fotoDocs del catálogo. */
  fotos: PerfilArchivoSlotState[] = [];
  /** Docs no-foto de SOBRE_NEGOCIO (paso documentos / info-adicional). */
  documentosSeccion: PerfilDocumentoDef[] = [];
  /** Fotos del negocio solo si vienen del catálogo API (flag foto). */
  fotoDocs: PerfilDocumentoDef[] = [];
  docsFiles: Record<string, PerfilArchivoSlotState> = {};
  introModalOpen = false;
  salirModalOpen = false;
  docModalOpen = false;
  docModalConfig: PerfilDocumentoModalConfig | null = null;
  private docModalValueKey: string | null = null;
  private pendingDocInput: HTMLInputElement | null = null;
  nombreNegocioHintOpen = false;
  nombreNegocioHintStyle: Record<string, string> = {};
  fechaCamaraHintOpen = false;
  fechaCamaraHintStyle: Record<string, string> = {};
  readonly fechaActual = new Date().toLocaleDateString('en-CA');

  /**
   * Subpasos: ubicación → fotos (si hay) → información adicional.
   * Controles de docs se ajustan al catálogo API en applyDocumentos().
   */
  steps: SobreNegocioStep[] = [];
  currentStep = 0;

  readonly idBogota = ID_BOGOTA;
  departamentos: CatalogoItem[] = [];
  ciudades: CatalogoItem[] = [];
  localidades: ReadonlyArray<Localidad> = LOCALIDADES_BOGOTA;
  tiposVivienda: CatalogoItem[] = [];

  @ViewChild('sectionScroll') sectionScroll?: ElementRef<HTMLElement>;
  @ViewChildren('fotoInput') fotoInputs?: QueryList<ElementRef<HTMLInputElement>>;
  @ViewChildren('docInput') docInputs?: QueryList<ElementRef<HTMLInputElement>>;

  private progressSub?: Subscription;
  private docsSub?: Subscription;
  private redesSub?: Subscription;
  private modalSub?: Subscription;
  private readonly loadSub = new Subscription();
  private saving = false;
  private pendingPaso: number | null = null;
  /** Evita que refresh/val de selectpicker borre ciudad al hidratar. */
  private hydratingUbicacion = true;
  private departamentosCargados = false;
  private pendingEmpUbicacion: PerfilEmpresarioCache | null | undefined;
  private ubicacionHydrateStarted = false;
  private readonly dirtyTracker = new PerfilSectionDirtyTracker();

  /** Capturado en constructor: getCurrentNavigation solo vive durante la nav. */
  private readonly showIntroOnEnter: boolean;

  constructor(
    private readonly fb: UntypedFormBuilder,
    private readonly userService: UserService,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly progress: PerfilProgressService,
    private readonly segmentoService: PerfilSegmentoService,
    private readonly documentosService: PerfilDocumentosService,
    private readonly parametrosService: ParametrosService,
    private readonly solicitudService: PerfilSolicitudService,
    private readonly seccionAcceso: PerfilSeccionAccesoService,
    private readonly sweetAlert: SweetAlertService,
    private readonly analyticsTag: AnalyticsTagService
  ) {
    this.showIntroOnEnter = !!readPerfilNavState(this.router)[
      PERFIL_NAV_SHOW_INTRO
    ];
  }

  ngOnInit(): void {
    this.userName = this.userService.getUser()?.nombre ?? '';
    this.form = this.fb.group({
      nombreNegocio: ['', Validators.required],
      departamento: ['', Validators.required],
      ciudad: ['', Validators.required],
      localidad: [''],
      barrio: ['', Validators.required],
      direccion: ['', Validators.required],
      tipoVivienda: ['', Validators.required],
      tieneRut: ['', Validators.required],
      tieneCamaraComercio: ['', Validators.required],
      fechaRegistroCamara: [''],
    });
    this.rebuildSteps();
    this.docsSub = this.documentosService.ensureLoaded().subscribe((docs) => {
      this.applyDocumentos(
        docs.filter((d) => d.seccionId === 'sobre-negocio')
      );
      this.precargarDesdeBackend();
    });
    this.cargarCatalogosUbicacion();
    this.syncFechaCamaraValidators(this.form.get('tieneCamaraComercio')?.value);
    this.syncLocalidadValidators();
    this.syncProgress();
    this.progressSub = this.form.valueChanges.subscribe(() => {
      this.syncProgress();
    });
    this.progressSub.add(
      this.form
        .get('tieneCamaraComercio')!
        .valueChanges.subscribe((value) => this.syncFechaCamaraValidators(value))
    );
    this.progressSub.add(
      this.form
        .get('fechaRegistroCamara')!
        .valueChanges.subscribe(() => this.revalidateFechaCamaraAntiguedad())
    );
    this.progressSub.add(
      this.form
        .get('ciudad')!
        .valueChanges.subscribe(() => this.syncLocalidadValidators())
    );
    this.progressSub.add(
      this.segmentoService.ensureParamsLoaded().subscribe(() => {
        this.syncFechaCamaraValidators(
          this.form.get('tieneCamaraComercio')?.value
        );
      })
    );
    this.cargarRedesSociales();
    this.loadSub.add(
      this.route.queryParamMap.subscribe((params) => {
        this.pendingPaso = parsePerfilPasoQuery(params.get(PERFIL_PASO_QUERY));
        this.applyPendingPaso();
      })
    );
  }

  ngOnDestroy(): void {
    this.progressSub?.unsubscribe();
    this.docsSub?.unsubscribe();
    this.redesSub?.unsubscribe();
    this.modalSub?.unsubscribe();
    this.loadSub.unsubscribe();
  }

  acceptFor(doc: PerfilDocumentoDef): string {
    if (isMultimediaDoc(doc)) {
      return acceptFromExtensiones(doc.extensionesValidas) || 'video/*,image/*';
    }
    return acceptFromExtensiones(doc.extensionesValidas);
  }

  labelDoc(doc: PerfilDocumentoDef): string {
    return labelConOpcional(doc.label, doc.obligatorio);
  }

  get currentStepId(): string {
    return this.steps[this.currentStep]?.id ?? '';
  }

  private applyDocumentos(docs: PerfilDocumentoDef[]): void {
    this.fotoDocs = docs
      .filter(
        (d) => d.esFoto || d.pasoId === PERFIL_DOC_PASO.fotos
      )
      .sort((a, b) => (a.orden ?? 0) - (b.orden ?? 0));
    this.documentosSeccion = docs
      .filter((d) => !(d.esFoto || d.pasoId === PERFIL_DOC_PASO.fotos))
      .sort((a, b) => (a.orden ?? 0) - (b.orden ?? 0));
    pruneStaleDocControls(this.form, this.docsFiles, [
      ...this.fotoDocs,
      ...this.documentosSeccion,
    ]);
    this.pruneStaleDocSlots(this.documentosSeccion);

    while (this.fotos.length < this.fotoDocs.length) {
      this.fotos.push({ name: '', sizeLabel: '' });
    }
    this.fotos = this.fotos.slice(0, Math.max(this.fotoDocs.length, 0));

    for (const doc of [...this.fotoDocs, ...this.documentosSeccion]) {
      if (!this.form.contains(doc.valueKey)) {
        this.form.addControl(
          doc.valueKey,
          this.fb.control('', doc.obligatorio ? Validators.required : [])
        );
      } else {
        const ctrl = this.form.get(doc.valueKey);
        if (doc.obligatorio) {
          ctrl?.setValidators([Validators.required]);
        } else {
          ctrl?.clearValidators();
        }
        ctrl?.updateValueAndValidity({ emitEvent: false });
      }
      if (!doc.esFoto) {
        for (const slot of slotsForDoc(doc)) {
          if (!this.docsFiles[slot.slotKey]) {
            this.docsFiles[slot.slotKey] = emptyArchivoSlot();
          }
        }
      }
    }

    this.rebuildSteps();
    this.syncProgress();
  }

  private pruneStaleDocSlots(keepDocs: ReadonlyArray<PerfilDocumentoDef>): void {
    const keep = new Set<string>();
    for (const doc of keepDocs) {
      for (const slot of slotsForDoc(doc)) {
        keep.add(slot.slotKey);
      }
    }
    for (const key of Object.keys(this.docsFiles)) {
      if (key.startsWith('doc_') && !keep.has(key)) {
        delete this.docsFiles[key];
      }
    }
  }

  slotsFor(doc: PerfilDocumentoDef): PerfilDocSlot[] {
    return slotsForDoc(doc);
  }

  isDocMultiple(doc: PerfilDocumentoDef): boolean {
    return isMultipleDoc(doc);
  }

  maxSlotsCount(doc: PerfilDocumentoDef): number {
    return cantidadSlotsDoc(doc);
  }

  filledSlots(doc: PerfilDocumentoDef): PerfilDocSlot[] {
    return slotsForDoc(doc).filter((s) => this.hasSlotDoc(s.slotKey));
  }

  filledSlotsCount(doc: PerfilDocumentoDef): number {
    return this.filledSlots(doc).length;
  }

  nextEmptySlot(doc: PerfilDocumentoDef): PerfilDocSlot | null {
    return slotsForDoc(doc).find((s) => !this.hasSlotDoc(s.slotKey)) ?? null;
  }

  multiUploadCta(doc: PerfilDocumentoDef): string {
    return this.filledSlotsCount(doc) === 0
      ? this.copy.sobreNegocio.cargarArchivos
      : this.copy.sobreNegocio.cargarOtroArchivo;
  }

  multiUploadHint(doc: PerfilDocumentoDef): string {
    return this.filledSlotsCount(doc) === 0
      ? this.copy.sobreNegocio.puedesSubirVarios
      : this.copy.sobreNegocio.archivosCargados;
  }

  private findDocSeccionBySlotOrValueKey(
    slotOrValueKey: string
  ): PerfilDocumentoDef | undefined {
    const base = baseValueKeyFromSlot(slotOrValueKey);
    return this.documentosSeccion.find((d) => d.valueKey === base);
  }

  private syncDocProgressControl(doc: PerfilDocumentoDef): void {
    const filled = slotsForDoc(doc).find((s) =>
      isArchivoSlotValidoParaProgreso(this.docsFiles[s.slotKey])
    );
    const name = filled ? this.docsFiles[filled.slotKey].name : '';
    this.form.get(doc.valueKey)?.setValue(name, { emitEvent: false });
    this.form.get(doc.valueKey)?.markAsTouched();
    this.form.get(doc.valueKey)?.updateValueAndValidity({ emitEvent: false });
  }

  private rebuildSteps(): void {
    const next: SobreNegocioStep[] = [
      {
        id: 'ubicacion',
        controls: [
          'nombreNegocio',
          'departamento',
          'ciudad',
          'barrio',
          'direccion',
          'tipoVivienda',
          'tieneRut',
          'tieneCamaraComercio',
        ],
      },
    ];
    if (this.fotoDocs.length) {
      next.push({
        id: 'fotos',
        controls: this.fotoDocs
          .filter((d) => d.obligatorio)
          .map((d) => d.valueKey),
      });
    }
    next.push({
      id: 'info-adicional',
      controls: this.documentosSeccion
        .filter((d) => d.obligatorio)
        .map((d) => d.valueKey),
    });
    const prevId = this.steps[this.currentStep]?.id;
    this.steps = next;
    if (this.pendingPaso != null) {
      this.applyPendingPaso();
      return;
    }
    const idx = prevId ? next.findIndex((s) => s.id === prevId) : 0;
    this.currentStep = idx >= 0 ? idx : 0;
  }

  private applyPendingPaso(): void {
    if (this.pendingPaso == null || !this.steps.length) {
      return;
    }
    const nextStep = Math.min(this.pendingPaso, this.steps.length - 1);
    this.pendingPaso = null;
    if (nextStep === this.currentStep) {
      if (this.steps[nextStep]?.id === 'ubicacion') {
        this.scheduleRebindUbicacionSelectpickers();
      }
      return;
    }
    this.currentStep = nextStep;
    this.scrollFormToTop();
    if (this.currentStepId === 'ubicacion') {
      this.scheduleRebindUbicacionSelectpickers();
    }
  }

  get sectionPercent(): number {
    return this.progress.getSectionProgress('sobre-negocio').percent;
  }

  get isLastStep(): boolean {
    return this.currentStep >= this.steps.length - 1;
  }

  get sectionDirty(): boolean {
    return this.dirtyTracker.isDirty(() => this.serializeSectionState());
  }

  get ctaKind(): 'continuar' | 'guardar' {
    return resolvePerfilStepCtaKind(
      this.isLastStep,
      this.sectionPercent,
      this.sectionDirty
    );
  }

  get ctaLabel(): string {
    return this.ctaKind === 'continuar'
      ? this.copy.botones.continuar
      : this.copy.botones.guardarInformacion;
  }

  get continuarDisabled(): boolean {
    if (this.saving) {
      return true;
    }
    if (this.isLastStep && this.ctaKind === 'continuar') {
      return false;
    }
    return !this.isCurrentStepValid();
  }

  /** Localidad solo aplica a Bogotá (igual que solicitud / actualizar-datos). */
  get muestraLocalidad(): boolean {
    return Number(this.form?.get('ciudad')?.value) === this.idBogota;
  }

  /** Muestra fecha de registro Cámara cuando responde Si. */
  get muestraFechaCamara(): boolean {
    return this.form?.get('tieneCamaraComercio')?.value === 'si';
  }

  /** PAR-073: mismo texto de tooltip / alerta que solicitud-natural. */
  get mensajeFechaRegistro(): string {
    return this.segmentoService.paramsSnapshot.mensajeFechaRegistro || '';
  }

  isInvalid(controlName: string): boolean {
    const control = this.form.get(controlName);
    return !!(control && control.invalid && (control.dirty || control.touched));
  }

  errorMessage(controlName: string): string {
    const ctrl = this.form.get(controlName);
    if (
      controlName === 'fechaRegistroCamara' &&
      ctrl?.hasError('fechaRegistroAntiguedad')
    ) {
      return (
        this.mensajeFechaRegistro ||
        this.copy.sobreNegocio.validaciones.fechaRegistroCamaraInvalida
      );
    }
    return this.copy.sobreNegocio.validaciones.campoObligatorio;
  }

  /** Tooltip fixed: queda por encima del set de pasos (no lo recorta el scroll). */
  showNombreNegocioHint(event: Event): void {
    const btn = event.currentTarget as HTMLElement | null;
    if (!btn) {
      return;
    }
    const rect = btn.getBoundingClientRect();
    this.nombreNegocioHintStyle = {
      top: `${Math.max(8, rect.top - 12)}px`,
      left: `${rect.left + rect.width / 2}px`,
    };
    this.nombreNegocioHintOpen = true;
  }

  hideNombreNegocioHint(): void {
    this.nombreNegocioHintOpen = false;
    this.fechaCamaraHintOpen = false;
  }

  showFechaCamaraHint(event: Event): void {
    const btn = event.currentTarget as HTMLElement | null;
    if (!btn || !this.mensajeFechaRegistro) {
      return;
    }
    const rect = btn.getBoundingClientRect();
    this.fechaCamaraHintStyle = {
      top: `${Math.max(8, rect.top - 12)}px`,
      left: `${rect.left + rect.width / 2}px`,
    };
    this.fechaCamaraHintOpen = true;
  }

  hasFoto(index: number): boolean {
    return isArchivoSlotValidoParaProgreso(this.fotos[index]);
  }

  hasFotoLoaded(index: number): boolean {
    return !!this.fotos[index]?.name;
  }

  /** Abre el modal unificado; solo con metadata del catálogo (sin inventar docs). */
  onFotoCargarClick(index: number): void {
    const doc = this.fotoDocs[index];
    if (!doc) {
      return;
    }
    this.openDocModal(doc, undefined, undefined, 'btn-perfil-foto');
  }

  /** Índice UI → índice en fotoInputs (mismo orden que fotoDocs). */
  fotoSlotIndex(valueKey: string): number {
    return this.fotoDocs.findIndex((d) => d.valueKey === valueKey);
  }

  triggerFotoUpload(index: number): void {
    const input = this.fotoInputs?.get(index)?.nativeElement;
    if (!input) {
      return;
    }
    input.value = '';
    input.click();
  }

  onFotoSelected(index: number, event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    const doc = this.fotoDocs[index];
    if (!file || !doc) {
      return;
    }
    this.subirArchivo(
      file,
      doc,
      true,
      (res) => {
        this.fotos[index] = slotStateFromUploadResult(file, res);
        const ok = isArchivoSlotValidoParaProgreso(this.fotos[index]);
        this.form
          .get(doc.valueKey)
          ?.setValue(ok ? this.fotos[index].name : '', { emitEvent: false });
        this.form.get(doc.valueKey)?.markAsTouched();
        this.form.get(doc.valueKey)?.updateValueAndValidity({ emitEvent: false });
        if (this.docModalOpen) {
          this.closeDocModal();
        }
      },
      this.fotos[index]?.name || undefined
    );
  }

  removeFoto(index: number): void {
    if (!this.canRemoveFoto(index)) {
      return;
    }
    const doc = this.fotoDocs[index];
    const idDocumento = this.fotos[index]?.idDocumento;
    const clearLocal = (): void => {
      this.fotos[index] = emptyArchivoSlot();
      if (doc) {
        this.form.get(doc.valueKey)?.setValue('', { emitEvent: false });
        this.form.get(doc.valueKey)?.markAsTouched();
        this.form.get(doc.valueKey)?.updateValueAndValidity({ emitEvent: false });
      }
      const input = this.fotoInputs?.get(index)?.nativeElement;
      if (input) {
        input.value = '';
      }
    };
    if (idDocumento) {
      this.solicitudService
        .eliminarDocumento(idDocumento, {
          esFoto: true,
          idTipoDocumento: doc?.idTipoDocumento ?? doc?.id,
          nombreArchivo: this.fotos[index]?.name || null,
        })
        .subscribe((res) => {
          if (!res.exitoso) {
            void this.sweetAlert.errorModal(
              'Error!',
              true,
              res.error || 'No se pudo eliminar la foto'
            );
            return;
          }
          clearLocal();
          this.syncProgress();
          this.solicitudService.persistirCamposProgreso().subscribe();
        });
      return;
    }
    clearLocal();
    this.syncProgress();
    this.solicitudService.persistirCamposProgreso().subscribe();
  }

  hasDoc(key: string): boolean {
    const doc = this.findDocSeccionBySlotOrValueKey(key);
    if (!doc) {
      return isArchivoSlotValidoParaProgreso(this.docsFiles[key]);
    }
    return slotsForDoc(doc).some((s) =>
      isArchivoSlotValidoParaProgreso(this.docsFiles[s.slotKey])
    );
  }

  hasSlotDoc(slotKey: string): boolean {
    return !!this.docsFiles[slotKey]?.name;
  }

  isSlotRechazado(slotKey: string): boolean {
    return isArchivoSlotRechazado(this.docsFiles[slotKey]);
  }

  isSlotAprobadoUi(slotKey: string): boolean {
    return shouldShowArchivoSlotAprobadoUi(
      this.docsFiles[slotKey],
      this.solicitudService.idEstadoSolicitud
    );
  }

  canRemoveSlot(slotKey: string): boolean {
    return canRemoveArchivoSlot(
      this.docsFiles[slotKey],
      this.solicitudService.idEstadoSolicitud
    );
  }

  isFotoRechazada(index: number): boolean {
    return isArchivoSlotRechazado(this.fotos[index]);
  }

  isFotoAprobadaUi(index: number): boolean {
    return shouldShowArchivoSlotAprobadoUi(
      this.fotos[index],
      this.solicitudService.idEstadoSolicitud
    );
  }

  canRemoveFoto(index: number): boolean {
    return canRemoveArchivoSlot(
      this.fotos[index],
      this.solicitudService.idEstadoSolicitud
    );
  }

  slotStatusLabel(slotKey: string): string {
    if (this.isSlotRechazado(slotKey)) {
      return this.copy.sobreNegocio.fotosArchivoRechazado;
    }
    if (this.isSlotAprobadoUi(slotKey)) {
      return this.copy.sobreNegocio.fotosArchivoAprobado;
    }
    return this.copy.sobreNegocio.fotosArchivoSubido;
  }

  fotoStatusLabel(index: number): string {
    if (this.isFotoRechazada(index)) {
      return this.copy.sobreNegocio.fotosArchivoRechazado;
    }
    if (this.isFotoAprobadaUi(index)) {
      return this.copy.sobreNegocio.fotosArchivoAprobado;
    }
    return this.copy.sobreNegocio.fotosArchivoSubido;
  }

  slotObservacion(slotKey: string): string {
    return String(this.docsFiles[slotKey]?.observacionDocumentoOCR || '').trim();
  }

  fotoObservacion(index: number): string {
    return String(this.fotos[index]?.observacionDocumentoOCR || '').trim();
  }

  /** Todo documento abre el modal unificado con info del catálogo. */
  onDocCargarClick(
    doc: PerfilDocumentoDef,
    slotKeyForUpload?: string,
    inputEl?: HTMLInputElement
  ): void {
    this.openDocModal(doc, slotKeyForUpload, inputEl);
  }

  openDocModal(
    doc: PerfilDocumentoDef,
    slotKeyForUpload?: string,
    inputEl?: HTMLInputElement,
    ctaElementIdPrefix: string = 'btn-perfil-doc'
  ): void {
    const resolvedSlotKey = slotKeyForUpload || doc.valueKey;
    this.analyticsTag.trackCtaClick({
      ctaLabel: doc.label,
      ctaSection: 'sobre-negocio',
      ctaComponent: 'perfil-sobre-negocio',
      ctaElementId: `${ctaElementIdPrefix}-${resolvedSlotKey}`,
      hu: '8099',
    });
    this.modalSub?.unsubscribe();
    this.docModalValueKey = slotKeyForUpload || doc.valueKey;
    this.pendingDocInput = inputEl ?? null;
    this.modalSub = this.documentosService.modalConfigFor(doc).subscribe((cfg) => {
      this.docModalConfig = cfg;
      this.docModalOpen = true;
    });
  }

  closeDocModal(): void {
    this.docModalOpen = false;
    this.docModalConfig = null;
    this.docModalValueKey = null;
    this.pendingDocInput = null;
  }

  /** CTA del modal: abre el file picker sin cerrar. */
  onSubirDesdeDocModal(): void {
    if (!this.docModalValueKey) {
      return;
    }
    const fotoIndex = this.fotoSlotIndex(
      baseValueKeyFromSlot(this.docModalValueKey)
    );
    if (fotoIndex >= 0) {
      this.triggerFotoUpload(fotoIndex);
      return;
    }
    if (this.pendingDocInput) {
      this.pendingDocInput.value = '';
      this.pendingDocInput.click();
      return;
    }
    this.triggerDocUpload(this.docModalValueKey);
  }

  triggerDocUpload(valueKey: string): void {
    const index = this.documentosSeccion.findIndex((d) => d.valueKey === valueKey);
    const input = this.docInputs?.get(index)?.nativeElement;
    if (!input) {
      return;
    }
    input.value = '';
    input.click();
  }

  onDocSelected(slotOrValueKey: string, event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) {
      return;
    }
    const doc = this.findDocSeccionBySlotOrValueKey(slotOrValueKey);
    if (!doc) {
      return;
    }
    const slotKey = slotOrValueKey;
    this.subirArchivo(
      file,
      doc,
      !!doc.esFoto,
      (res) => {
        this.docsFiles[slotKey] = slotStateFromUploadResult(file, res);
        this.syncDocProgressControl(doc);
        if (this.docModalOpen && this.docModalValueKey === slotKey) {
          this.closeDocModal();
        }
      },
      this.docsFiles[slotKey]?.name || undefined
    );
  }

  removeDoc(slotOrValueKey: string, inputEl?: HTMLInputElement): void {
    const doc = this.findDocSeccionBySlotOrValueKey(slotOrValueKey);
    const slotKey = slotOrValueKey;
    if (!this.canRemoveSlot(slotKey)) {
      return;
    }
    const idDocumento = this.docsFiles[slotKey]?.idDocumento;
    const clearLocal = (): void => {
      this.docsFiles[slotKey] = emptyArchivoSlot();
      if (doc) {
        this.syncDocProgressControl(doc);
      }
      if (inputEl) {
        inputEl.value = '';
      }
    };
    if (idDocumento) {
      this.solicitudService
        .eliminarDocumento(idDocumento, {
          esFoto: !!doc?.esFoto,
          idTipoDocumento: doc?.idTipoDocumento ?? doc?.id,
          nombreArchivo: this.docsFiles[slotKey]?.name || null,
        })
        .subscribe((res) => {
          if (!res.exitoso) {
            void this.sweetAlert.errorModal(
              'Error!',
              true,
              res.error || 'No se pudo eliminar el documento'
            );
            return;
          }
          clearLocal();
          this.syncProgress();
          this.solicitudService.persistirCamposProgreso().subscribe();
        });
      return;
    }
    clearLocal();
    this.syncProgress();
    this.solicitudService.persistirCamposProgreso().subscribe();
  }

  isRedChecked(id: string): boolean {
    return !!this.form.get(id)?.value;
  }

  /**
   * Labels: intermedios = Continuar; último = Guardar (o Continuar si 100 % sin dirty).
   * Back: solo si hay cambios. Sin dirty → avanza/navega sin request.
   */
  onCta(): void {
    if (this.saving) {
      return;
    }
    this.markCurrentStepTouched();
    if (
      this.currentStepId === 'ubicacion' &&
      this.muestraFechaCamara &&
      this.form.get('fechaRegistroCamara')?.hasError('fechaRegistroAntiguedad')
    ) {
      void this.sweetAlert.errorModal(
        'Error!',
        true,
        this.mensajeFechaRegistro ||
          this.copy.sobreNegocio.validaciones.fechaRegistroCamaraInvalida
      );
      return;
    }
    if (this.ctaKind !== 'continuar' || !this.isLastStep) {
      if (!this.isCurrentStepValid()) {
        return;
      }
    }
    this.syncProgress();
    if (!this.sectionDirty) {
      if (this.isLastStep) {
        this.goToNextSection();
        return;
      }
      this.currentStep += 1;
      this.scrollFormToTop();
      return;
    }
    if (this.currentStepId === 'ubicacion') {
      this.guardarUbicacionYAvanzar();
      return;
    }
    if (this.isLastStep) {
      this.guardarRedesYContinuar();
      return;
    }
    this.currentStep += 1;
    this.scrollFormToTop();
  }

  onDepartamentoChange(event: Event): void {
    if (this.hydratingUbicacion) {
      return;
    }
    const value = (event.target as HTMLSelectElement)?.value;
    this.refreshSelectpicker('departamento');
    this.resetDependentSelect('ciudad');
    this.resetDependentSelect('localidad');
    this.ciudades = [];
    this.refreshSelectpicker('ciudad');
    this.refreshSelectpicker('localidad');
    if (value) {
      this.getCiudades(Number(value));
    }
  }

  onCiudadChange(): void {
    if (this.hydratingUbicacion) {
      return;
    }
    const ciudad = this.form.get('ciudad');
    ciudad?.updateValueAndValidity({ emitEvent: false });
    ciudad?.markAsTouched();
    this.refreshSelectpicker('ciudad');
    setTimeout(() => this.syncSelectpickerInvalidClass('ciudad'), 80);
    this.resetDependentSelect('localidad');
    this.syncLocalidadValidators();
    this.refreshSelectpicker('localidad');
  }

  /** Limpia hijo (ciudad/localidad) y lo deja required+rojo hasta que elijan. */
  private resetDependentSelect(field: 'ciudad' | 'localidad'): void {
    const ctrl = this.form.get(field);
    if (!ctrl) {
      return;
    }
    ctrl.setValue('');
    ctrl.markAsTouched();
    ctrl.markAsDirty();
    ctrl.updateValueAndValidity({ emitEvent: false });
  }

  onLocalidadChange(): void {
    this.refreshSelectpicker('localidad');
  }

  onTipoViviendaChange(): void {
    this.refreshSelectpicker('tipoVivienda');
  }

  onSaltar(): void {
    this.salirModalOpen = true;
  }

  closeSalirModal(): void {
    this.salirModalOpen = false;
  }

  private buildRedesPayload(): Array<{
    url: string;
    idCatalogoRedSocial: number | null;
  }> {
    const redesSocialesDetalle: Array<{
      url: string;
      idCatalogoRedSocial: number | null;
    }> = [];
    for (const red of this.redesItems) {
      if (!this.form.get(red.id)?.value) {
        continue;
      }
      const url = String(this.form.get(`${red.id}Url`)?.value ?? '').trim();
      if (!url) {
        continue;
      }
      redesSocialesDetalle.push({
        url: normalizeRedSocialUrl(url),
        idCatalogoRedSocial:
          red.idCatalogo != null ? Number(red.idCatalogo) : null,
      });
    }
    return redesSocialesDetalle;
  }

  onSalirGuardar(): void {
    this.salirModalOpen = false;
    this.syncProgress();
    const redesSocialesDetalle = this.buildRedesPayload();
    const v = this.form.getRawValue();
    this.solicitudService
      .guardarBorrador({
        idDepartamento: Number(v.departamento) || null,
        idCiudad: Number(v.ciudad) || null,
        idLocalidad: this.muestraLocalidad ? Number(v.localidad) || null : null,
        barrioResidencia: v.barrio,
        direccionResidencia: v.direccion,
        idTipoVivienda: Number(v.tipoVivienda) || null,
        razonSocial: v.nombreNegocio,
        tieneRUT: v.tieneRut === 'si' ? '1' : '0',
        camaraComercioVigente: v.tieneCamaraComercio === 'si' ? 1 : 0,
        fechaRegistroActividadEconomica:
          v.tieneCamaraComercio === 'si' ? v.fechaRegistroCamara : null,
        redesSocialesDetalle,
      })
      .subscribe(() => {
        void this.router.navigateByUrl(PERFIL_HUB_ROUTE);
      });
  }

  closeIntroModal(): void {
    this.introModalOpen = false;
  }

  onIntroCompletarDatos(): void {
    this.closeIntroModal();
  }

  private maybeOpenIntroModal(): void {
    if (!this.showIntroOnEnter) {
      return;
    }
    if (!this.seccionAcceso.shouldShowIntro('sobre-negocio')) {
      consumeShowIntroFlag(readPerfilNavState(this.router));
      return;
    }
    this.seccionAcceso.markIntroShown('sobre-negocio');
    this.introModalOpen = true;
    consumeShowIntroFlag(readPerfilNavState(this.router));
  }

  private syncFechaCamaraValidators(value: unknown): void {
    const ctrl = this.form.get('fechaRegistroCamara');
    if (!ctrl) {
      return;
    }
    if (value === 'si') {
      ctrl.setValidators([
        Validators.required,
        this.fechaRegistroAntiguedadValidator(),
      ]);
    } else {
      ctrl.clearValidators();
      ctrl.setValue('', { emitEvent: false });
    }
    ctrl.updateValueAndValidity({ emitEvent: false });
  }

  private revalidateFechaCamaraAntiguedad(): void {
    const ctrl = this.form.get('fechaRegistroCamara');
    if (!ctrl || this.form.get('tieneCamaraComercio')?.value !== 'si') {
      return;
    }
    ctrl.updateValueAndValidity({ emitEvent: false });
  }

  /** Misma regla que validarFechaRegistro (PAR-070). */
  private fechaRegistroAntiguedadValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const raw = control.value;
      if (!raw) {
        return null;
      }
      const meses =
        this.segmentoService.paramsSnapshot.fechaRegistroMinimaMeses ?? 0;
      return isFechaRegistroCamaraInvalida(String(raw), meses)
        ? { fechaRegistroAntiguedad: true }
        : null;
    };
  }

  private cargarRedesSociales(): void {
    this.redesSub = this.parametrosService
      .getRedesSocialesPerfil()
      .pipe(catchError(() => of([])))
      .subscribe((rows) => {
        const list = this.extraerListaRedes(rows);
        this.applyRedes(this.mapRedesFromApi(list));
        this.applyRedesFromBackend();
        this.syncAllRedUrlValidators();
        this.syncProgress();
        this.markSectionBaseline();
        this.scheduleBaselineAfterUiSettle();
      });
  }

  /** Acepta array plano o NeoResponseDTO `{ data: [] }`. */
  private extraerListaRedes(res: unknown): CatalogoRedSocialApi[] {
    if (Array.isArray(res)) {
      return res as CatalogoRedSocialApi[];
    }
    if (
      res &&
      typeof res === 'object' &&
      Array.isArray((res as { data?: unknown }).data)
    ) {
      return (res as { data: CatalogoRedSocialApi[] }).data;
    }
    return [];
  }

  private mapRedesFromApi(rows: CatalogoRedSocialApi[]): RedSocialItem[] {
    return [...rows]
      .sort((a, b) => (a.orden ?? 0) - (b.orden ?? 0))
      .map((row, index) => {
        const id =
          row.codigo ||
          (row.idCatalogoRedSocial != null
            ? String(row.idCatalogoRedSocial)
            : `red_${index}`);
        return {
          id,
          label: row.nombre || id,
          idCatalogo: row.idCatalogoRedSocial,
          esOtro: !!row.esOtro,
        };
      });
  }

  private applyRedes(list: RedSocialItem[]): void {
    for (const red of this.redesItems) {
      if (this.form.contains(red.id)) {
        this.form.removeControl(red.id);
      }
      if (this.form.contains(`${red.id}Url`)) {
        this.form.removeControl(`${red.id}Url`);
      }
    }
    this.redesItems = list;
    for (const red of this.redesItems) {
      this.form.addControl(red.id, this.fb.control(false));
      this.form.addControl(`${red.id}Url`, this.fb.control(''));
      this.progressSub?.add(
        this.form
          .get(red.id)!
          .valueChanges.subscribe((checked) =>
            this.syncRedUrlValidators(red.id, !!checked)
          )
      );
    }
  }

  private syncAllRedUrlValidators(): void {
    for (const red of this.redesItems) {
      this.syncRedUrlValidators(red.id, !!this.form.get(red.id)?.value);
    }
  }

  /** Si la red está marcada, la URL es obligatoria; si no, se limpia. */
  private syncRedUrlValidators(redId: string, checked: boolean): void {
    const urlCtrl = this.form.get(`${redId}Url`);
    if (!urlCtrl) {
      return;
    }
    if (checked) {
      urlCtrl.setValidators([Validators.required]);
    } else {
      urlCtrl.clearValidators();
      urlCtrl.setValue('');
      urlCtrl.markAsUntouched();
    }
    urlCtrl.updateValueAndValidity({ emitEvent: false });
  }

  private isCurrentStepValid(): boolean {
    const step = this.steps[this.currentStep];
    if (!step) {
      return false;
    }
    if (step.id === 'info-adicional') {
      return this.areCheckedRedUrlsValid();
    }
    const keys = [...step.controls];
    if (step.id === 'ubicacion' && this.muestraLocalidad) {
      keys.push('localidad');
    }
    const baseOk = keys.every((key) => this.form.get(key)?.valid);
    if (!baseOk) {
      return false;
    }
    if (step.id === 'ubicacion' && this.muestraFechaCamara) {
      const fechaCtrl = this.form.get('fechaRegistroCamara');
      if (!fechaCtrl?.value || fechaCtrl.hasError('required')) {
        return false;
      }
    }
    return true;
  }

  private areCheckedRedUrlsValid(): boolean {
    return this.redesItems.every((red) => {
      if (!this.form.get(red.id)?.value) {
        return true;
      }
      return !!this.form.get(`${red.id}Url`)?.valid;
    });
  }

  private markCurrentStepTouched(): void {
    const step = this.steps[this.currentStep];
    if (!step) {
      return;
    }
    for (const key of step.controls) {
      this.form.get(key)?.markAsTouched();
    }
    if (step.id === 'ubicacion' && this.muestraLocalidad) {
      this.form.get('localidad')?.markAsTouched();
    }
    if (step.id === 'ubicacion' && this.muestraFechaCamara) {
      this.form.get('fechaRegistroCamara')?.markAsTouched();
    }
    if (step.id === 'info-adicional') {
      for (const red of this.redesItems) {
        if (this.form.get(red.id)?.value) {
          this.form.get(`${red.id}Url`)?.markAsTouched();
        }
      }
    }
  }

  private goToNextSection(): void {
    const nextId = resolveNextSectionId('sobre-negocio');
    const nextPercent = nextId
      ? Math.max(
          this.seccionAcceso.sectionPercent(nextId),
          this.progress.getSectionProgress(nextId).percent
        )
      : 0;
    navigateAfterSectionSave(this.router, 'sobre-negocio', nextPercent);
  }

  private scrollFormToTop(): void {
    const el = this.sectionScroll?.nativeElement;
    if (el) {
      el.scrollTop = 0;
    }
  }

  private syncProgress(): void {
    this.progress.updateSectionValues(
      'sobre-negocio',
      this.form.getRawValue(),
      invalidFilledControlKeys(this.form)
    );
  }

  private applyRedesFromBackend(): void {
    const fromCarga = Array.isArray(this.solicitudService.redesDetalleSnapshot)
      ? this.solicitudService.redesDetalleSnapshot
      : [];
    if (!fromCarga.length || !this.redesItems.length) {
      return;
    }
    for (const red of this.redesItems) {
      const match = findRedDetalleForCatalogo(
        fromCarga,
        red.idCatalogo ?? null,
        red.id
      );
      if (!match) {
        this.form.get(red.id)?.setValue(false, { emitEvent: false });
        this.form.get(`${red.id}Url`)?.setValue('', { emitEvent: false });
        continue;
      }
      this.form.get(red.id)?.setValue(true, { emitEvent: false });
      this.form.get(`${red.id}Url`)?.setValue(match.url, { emitEvent: false });
    }
    this.syncAllRedUrlValidators();
  }

  private syncLocalidadValidators(): void {
    const ctrl = this.form.get('localidad');
    if (!ctrl) {
      return;
    }
    if (this.muestraLocalidad) {
      ctrl.setValidators([Validators.required]);
    } else {
      ctrl.clearValidators();
      ctrl.setValue('', { emitEvent: false });
    }
    ctrl.updateValueAndValidity({ emitEvent: false });
  }

  private cargarCatalogosUbicacion(): void {
    this.parametrosService.getDepartamentos('169').subscribe({
      next: (data: any) => {
        this.departamentos = (data?.data ?? []).map(
          (d: { idDepartamento: number; nombreDepartamento: string }) => ({
            id: d.idDepartamento,
            nombre: d.nombreDepartamento,
          })
        );
        this.departamentosCargados = true;
        this.tryHydrateUbicacion();
      },
      error: () => {
        this.departamentosCargados = true;
        this.tryHydrateUbicacion();
        void this.sweetAlert.errorModal(
          'Error!',
          true,
          'Error al cargar los departamentos'
        );
      },
    });
    this.parametrosService.getTipoVivienda().subscribe({
      next: (data: any) => {
        const list = Array.isArray(data) ? data : data?.data ?? [];
        this.tiposVivienda = list.map(
          (t: { idTipoVivienda: number; nombre: string }) => ({
            id: t.idTipoVivienda,
            nombre: t.nombre,
          })
        );
        setTimeout(() => {
          this.refreshSelectpicker('tipoVivienda');
          const emp = this.pendingEmpUbicacion;
          if (emp?.idTipoVivienda) {
            this.setSelectpickerVal('tipoVivienda', emp.idTipoVivienda);
          }
        }, 200);
      },
    });
  }

  private getCiudades(idDepartamento: number, onDone?: () => void): void {
    this.parametrosService.getCiudades(String(idDepartamento)).subscribe({
      next: (data: any) => {
        this.ciudades = (data?.data ?? []).map(
          (c: { idCiudad: number; nombreCiudad: string }) => ({
            id: c.idCiudad,
            nombre: c.nombreCiudad,
          })
        );
        setTimeout(() => {
          onDone?.();
          this.refreshSelectpicker('ciudad');
        }, 200);
      },
      error: () => {
        onDone?.();
        void this.sweetAlert.errorModal(
          'Error!',
          true,
          'Error al cargar las ciudades'
        );
      },
    });
  }

  private precargarDesdeBackend(): void {
    this.loadSub.add(
      this.solicitudService.ensureCargado(true).subscribe((carga) => {
        const emp = carga.empresario;
        if (!emp) {
          this.applyRedesFromBackend();
          this.pendingEmpUbicacion = null;
          this.tryHydrateUbicacion();
          this.precargarDocumentosCargados();
          return;
        }
        const patch = mapEmpresarioToSobreNegocio(emp);
        this.form.patchValue(patch, { emitEvent: false });
        this.syncFechaCamaraValidators(
          this.form.get('tieneCamaraComercio')?.value
        );
        this.applyRedesFromBackend();
        this.pendingEmpUbicacion = emp;
        this.tryHydrateUbicacion();
        markInvalidControlsTouched(this.form);
        this.syncProgress();
        this.precargarDocumentosCargados();
      })
    );
  }

  private precargarDocumentosCargados(): void {
    this.solicitudService.cargarDocumentosSolicitud().subscribe((data) => {
      const filtered = intersectSeccionDocsConSolicitud(
        this.documentosService.documentos,
        data,
        'sobre-negocio'
      );
      this.applyDocumentos(filtered);
      this.progress.replaceDocumentosDeSeccion('sobre-negocio', filtered);
      const applyList = (
        list: typeof data.archivos,
        docs: PerfilDocumentoDef[],
        isFoto: boolean
      ): void => {
        for (const item of list ?? []) {
          const def = docs.find(
            (d) => Number(d.idTipoDocumento ?? d.id) === Number(item.idTipoDocumento)
          );
          if (!def) {
            continue;
          }
          const files = (item.neoDocumentosSolicitudDTO ?? []).filter(
            (f) => !!f?.idDocumentoSolicitud
          );
          if (isFoto) {
            const uploaded = files[0];
            if (!uploaded) {
              continue;
            }
            const name =
              uploaded.nombreDocumento ||
              String(this.form.get(def.valueKey)?.value || 'Documento');
            this.form.get(def.valueKey)?.setValue(name, { emitEvent: false });
            const idx = this.fotoSlotIndex(def.valueKey);
            if (idx >= 0) {
              this.fotos[idx] = slotStateFromUploaded(uploaded, name);
              if (!isArchivoSlotValidoParaProgreso(this.fotos[idx])) {
                this.form.get(def.valueKey)?.setValue('', { emitEvent: false });
              }
            }
            continue;
          }
          const slots = slotsForDoc(def);
          slots.forEach((slot, idx) => {
            const uploaded = files[idx];
            if (!uploaded) {
              this.docsFiles[slot.slotKey] = emptyArchivoSlot();
              return;
            }
            this.docsFiles[slot.slotKey] = slotStateFromUploaded(
              uploaded,
              uploaded.nombreDocumento || 'Documento'
            );
          });
          this.syncDocProgressControl(def);
        }
      };
      applyList(data.archivos, this.documentosSeccion, false);
      applyList(data.fotos, this.fotoDocs, true);
      markInvalidControlsTouched(this.form);
      this.syncProgress();
      this.solicitudService.persistirCamposProgreso().subscribe();
      this.markSectionBaseline();
      this.scheduleBaselineAfterUiSettle();
      this.maybeOpenIntroModal();
    });
  }

  private serializeSectionState(): string {
    const docs: Record<
      string,
      { idDocumento?: number | string | null; name?: string }
    > = { ...this.docsFiles };
    this.fotoDocs.forEach((doc, i) => {
      const slot = this.fotos[i];
      if (doc?.valueKey) {
        docs[doc.valueKey] = {
          idDocumento: slot?.idDocumento,
          name: slot?.name,
        };
      }
    });
    return serializePerfilSectionState(this.form.getRawValue(), docs);
  }

  private markSectionBaseline(): void {
    this.dirtyTracker.markBaseline(() => this.serializeSectionState());
    this.form.markAsPristine();
  }

  private scheduleBaselineAfterUiSettle(): void {
    setTimeout(() => this.markSectionBaseline(), 150);
  }

  private resetDirtyAfterSave(): void {
    this.markSectionBaseline();
  }

  private guardarUbicacionYAvanzar(): void {
    this.saving = true;
    const v = this.form.getRawValue();
    this.solicitudService
      .guardarBorrador({
        idDepartamento: Number(v.departamento) || null,
        idCiudad: Number(v.ciudad) || null,
        idLocalidad: this.muestraLocalidad
          ? Number(v.localidad) || null
          : null,
        barrioResidencia: v.barrio,
        direccionResidencia: v.direccion,
        idTipoVivienda: Number(v.tipoVivienda) || null,
        razonSocial: v.nombreNegocio,
        tieneRUT: v.tieneRut === 'si' ? '1' : '0',
        camaraComercioVigente: v.tieneCamaraComercio === 'si' ? 1 : 0,
        fechaRegistroActividadEconomica:
          v.tieneCamaraComercio === 'si' ? v.fechaRegistroCamara : null,
      })
      .subscribe((res) => {
        this.saving = false;
        if (!res.exitoso) {
          void this.sweetAlert.errorModal(
            'Error!',
            true,
            res.error || 'No se pudo guardar la información'
          );
          return;
        }
        this.resetDirtyAfterSave();
        this.currentStep += 1;
        this.scrollFormToTop();
      });
  }

  private guardarRedesYContinuar(): void {
    const redesSocialesDetalle = this.buildRedesPayload();
    this.saving = true;
    this.solicitudService
      .guardarBorrador({ redesSocialesDetalle })
      .subscribe((res) => {
      this.saving = false;
      if (!res.exitoso) {
        void this.sweetAlert.errorModal(
          'Error!',
          true,
          res.error || 'No se pudo guardar la información'
        );
        return;
      }
      this.goToNextSection();
    });
  }

  private subirArchivo(
    file: File,
    doc: PerfilDocumentoDef,
    esFoto: boolean,
    onOk: (res: {
      idDocumentoSolicitud?: string | number;
      pesoArchivo?: number | null;
      habilitado?: number | null;
      estadoDocumentoOCR?: number | null;
      observacionDocumentoOCR?: string | null;
    }) => void,
    excludeNombreArchivo?: string
  ): void {
    this.solicitudService
      .subirDocumento(file, doc, { esFoto, excludeNombreArchivo })
      .subscribe((res) => {
      if (!res.exitoso) {
        void this.sweetAlert.errorModal(
          'Error!',
          true,
          res.error || 'No se pudo subir el archivo'
        );
        return;
      }
      if (res.estadoDocumentoOCR === 0) {
        void this.sweetAlert.errorModal(
          'Error!',
          true,
          res.error ||
            res.observacionDocumentoOCR ||
            this.copy.sobreNegocio.validaciones.documentosInconsistentes
        );
        onOk(res);
        this.syncProgress();
        this.solicitudService.persistirCamposProgreso().subscribe();
        return;
      }
      void this.sweetAlert.successModal('Archivo subido');
      onOk(res);
      this.syncProgress();
      this.solicitudService.persistirCamposProgreso().subscribe();
    });
  }

  private toInputDate(value: unknown): string {
    if (!value) {
      return '';
    }
    if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value)) {
      return value.slice(0, 10);
    }
    try {
      return new Date(value as string).toISOString().slice(0, 10);
    } catch {
      return '';
    }
  }

  private refreshSelectpicker(field: string): void {
    setTimeout(() => {
      this.initOrRefreshSelectpicker(field);
      this.syncSelectpickerInvalidClass(field);
    }, 50);
  }

  /**
   * El plugin mueve/copia clases NgClass del <select> al wrapper
   * (`perfil-field__control--invalid`). Hay que alinear wrapper+botón
   * con isInvalid() después del refresh.
   */
  private syncSelectpickerInvalidClass(field: string): void {
    const $el = $(`#${field}`);
    if (!$el?.length) {
      return;
    }
    const $wrap = $el.parent().hasClass('bootstrap-select')
      ? $el.parent()
      : $el.closest('.bootstrap-select');
    const $btn = $wrap.find('> button.dropdown-toggle');
    const showError = this.isInvalid(field);
    $el.toggleClass('is-invalid perfil-field__control--invalid', showError);
    $btn.toggleClass('is-invalid perfil-field__control--invalid', showError);
    if ($wrap?.length) {
      $wrap.toggleClass(
        'is-invalid perfil-field__control--invalid ng-invalid',
        showError
      );
      if (!showError) {
        $wrap.removeClass('ng-invalid');
      }
    }
  }

  private refreshAllSelectpickers(): void {
    setTimeout(() => {
      for (const field of [
        'departamento',
        'ciudad',
        'localidad',
        'tipoVivienda',
      ]) {
        this.initOrRefreshSelectpicker(field);
      }
    }, 50);
  }

  /**
   * Al volver al paso ubicación el @if recrea el <select>; bootstrap-select
   * hay que (re)inicializarlo o solo quedan los labels.
   */
  private initOrRefreshSelectpicker(field: string): void {
    const $el = $(`#${field}`);
    if (!$el?.length || typeof $el.selectpicker !== 'function') {
      return;
    }
    const wrapped = $el.parent().hasClass('bootstrap-select');
    if (wrapped) {
      $el.selectpicker('refresh');
    } else {
      $el.selectpicker({
        liveSearch: true,
        width: '100%',
        size: 5,
      });
    }
  }

  private setSelectpickerVal(field: string, value: unknown): void {
    if (value == null || value === '') {
      return;
    }
    const $el = $(`#${field}`);
    if ($el?.length && typeof $el.selectpicker === 'function') {
      this.initOrRefreshSelectpicker(field);
      $el.selectpicker('val', String(value));
    }
  }

  /** Tras recrear el DOM del paso 0, reaplicar vals del form a selectpicker. */
  private scheduleRebindUbicacionSelectpickers(): void {
    setTimeout(() => this.rebindUbicacionSelectpickersFromForm(), 0);
    setTimeout(() => this.rebindUbicacionSelectpickersFromForm(), 120);
  }

  private rebindUbicacionSelectpickersFromForm(): void {
    if (this.currentStepId !== 'ubicacion') {
      return;
    }
    const idDepto = this.form.get('departamento')?.value;
    const idCiudad = this.form.get('ciudad')?.value;
    const idLocalidad = this.form.get('localidad')?.value;
    const idTipo = this.form.get('tipoVivienda')?.value;

    const applyVals = (): void => {
      this.initOrRefreshSelectpicker('departamento');
      this.setSelectpickerVal('departamento', idDepto);
      this.initOrRefreshSelectpicker('ciudad');
      this.setSelectpickerVal('ciudad', idCiudad);
      if (this.muestraLocalidad) {
        this.initOrRefreshSelectpicker('localidad');
        this.setSelectpickerVal('localidad', idLocalidad);
      }
      this.initOrRefreshSelectpicker('tipoVivienda');
      this.setSelectpickerVal('tipoVivienda', idTipo);
    };

    if (idDepto && !this.ciudades.length) {
      this.getCiudades(Number(idDepto), applyVals);
      return;
    }
    applyVals();
  }

  private tryHydrateUbicacion(): void {
    if (this.ubicacionHydrateStarted) {
      return;
    }
    if (!this.departamentosCargados || this.pendingEmpUbicacion === undefined) {
      return;
    }
    this.ubicacionHydrateStarted = true;
    const emp = this.pendingEmpUbicacion;
    if (!emp?.idDepartamento) {
      this.finishUbicacionHydrate(emp);
      return;
    }
    const idDepto = String(emp.idDepartamento);
    const idCiudad = emp.idCiudad != null ? String(emp.idCiudad) : '';
    this.form.get('departamento')?.setValue(idDepto, { emitEvent: false });
    this.getCiudades(Number(idDepto), () => {
      if (idCiudad) {
        this.form.get('ciudad')?.setValue(idCiudad, { emitEvent: false });
        this.syncLocalidadValidators();
        if (Number(idCiudad) === this.idBogota && emp.idLocalidad) {
          this.form
            .get('localidad')
            ?.setValue(String(emp.idLocalidad), { emitEvent: false });
        }
      }
      this.applyUbicacionSelectpickers(emp, idDepto, idCiudad);
    });
  }

  private applyUbicacionSelectpickers(
    emp: PerfilEmpresarioCache,
    idDepto: string,
    idCiudad: string
  ): void {
    setTimeout(() => {
      this.setSelectpickerVal('departamento', idDepto);
      if (idCiudad) {
        this.setSelectpickerVal('ciudad', idCiudad);
      }
      if (Number(idCiudad) === this.idBogota && emp.idLocalidad) {
        this.setSelectpickerVal('localidad', emp.idLocalidad);
      }
      this.setSelectpickerVal('tipoVivienda', emp.idTipoVivienda);
      this.refreshSelectpicker('departamento');
      this.refreshSelectpicker('ciudad');
      this.refreshSelectpicker('localidad');
      this.refreshSelectpicker('tipoVivienda');
      setTimeout(() => {
        this.setSelectpickerVal('departamento', idDepto);
        if (idCiudad) {
          this.setSelectpickerVal('ciudad', idCiudad);
        }
        if (Number(idCiudad) === this.idBogota && emp.idLocalidad) {
          this.setSelectpickerVal('localidad', emp.idLocalidad);
        }
        this.setSelectpickerVal('tipoVivienda', emp.idTipoVivienda);
        this.finishUbicacionHydrate(emp);
      }, 120);
    }, 80);
  }

  private finishUbicacionHydrate(emp: PerfilEmpresarioCache | null): void {
    if (!emp) {
      setTimeout(() => this.refreshAllSelectpickers(), 200);
    }
    this.hydratingUbicacion = false;
    this.markSectionBaseline();
    this.scheduleBaselineAfterUiSettle();
  }
}
